import { useMemo } from 'react';
import { useNavigate } from 'react-router';
import { machineLiveness } from '@orgus/protocol';
import type { Job, Machine, Session } from '@orgus/protocol';
import { RingMeter, Sparkline, StatStrip, StatTile, StateBar, formatBytes, formatInt, formatPercent, plural } from '@orgus/ui';
import type { MachineSample } from '../../lib/store.ts';
import { CPU_SLOT, MEM_SLOT, sessionCountsText, sessionStates } from './MachineCard.tsx';

/** The fleet's average of one measure over time: sample k from the end, averaged over the machines that have it. */
export function fleetSeries(history: Record<string, MachineSample[] | undefined>, ids: readonly string[], pick: (s: MachineSample) => number, points = 40): number[] {
  const lists = ids.map((id) => history[id] ?? []).filter((l) => l.length > 0);
  if (!lists.length) return [];
  const len = Math.min(points, Math.max(...lists.map((l) => l.length)));
  const out: number[] = [];
  for (let k = len - 1; k >= 0; k--) {
    let sum = 0;
    let n = 0;
    for (const l of lists) {
      const s = l[l.length - 1 - k];
      if (s) {
        sum += pick(s);
        n += 1;
      }
    }
    if (n) out.push(sum / n);
  }
  return out;
}

/**
 * The fleet at a glance: machines online as a ring, average CPU and memory with their recent
 * trend, and the live sessions by state. Grey by default; only "not reporting" and "needs you"
 * are loud, and only when they happen.
 */
export function FleetGlance({ machines, history, sessions, jobs, now }: { machines: Machine[]; history: Record<string, MachineSample[] | undefined>; sessions: Session[]; jobs: Job[]; now: number }) {
  const navigate = useNavigate();
  const on = machines.filter((m) => machineLiveness(m, now) === 'online');
  const stale = machines.filter((m) => machineLiveness(m, now) === 'stale').length;
  const offline = machines.length - on.length - stale;
  const idsKey = on.map((m) => m.id).join(',');
  const ids = useMemo(() => (idsKey ? idsKey.split(',') : []), [idsKey]);
  const cpu = useMemo(() => fleetSeries(history, ids, (s) => s.load), [history, ids]);
  const mem = useMemo(() => fleetSeries(history, ids, (s) => s.mem), [history, ids]);
  const avg = (f: (m: Machine) => number) => (on.length ? on.reduce((a, m) => a + f(m), 0) / on.length : 0);
  const load = avg((m) => m.load);
  const used = avg((m) => (m.memTotal > 0 ? 1 - m.memFree / m.memTotal : 0));
  const busiest = [...on].sort((a, b) => b.load - a.load)[0];
  const live = sessions.filter((s) => s.status !== 'ended');
  const commands = jobs.filter((j) => j.state === 'running').length;
  const usedBytes = on.reduce((a, m) => a + (m.memTotal - m.memFree), 0);
  const totalBytes = on.reduce((a, m) => a + m.memTotal, 0);

  return (
    <StatStrip label="Fleet" className="grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
      <StatTile
        label="Machines online"
        featured
        value={on.length}
        of={machines.length}
        attention={stale > 0}
        sub={[stale ? `${formatInt(stale)} not reporting` : offline ? `${formatInt(offline)} offline` : 'Every machine reporting', commands ? `${plural(commands, 'command')} running` : ''].filter(Boolean).join(', ')}
        trend={<RingMeter value={machines.length ? on.length / machines.length : 0} size={40} tone={3} label={`${formatInt(on.length)} of ${formatInt(machines.length)} machines online`} />}
      />
      <StatTile
        label="Average CPU"
        value={load}
        format={(n) => formatPercent(n)}
        sub={busiest && on.length > 1 ? `Busiest ${busiest.name}, ${formatPercent(busiest.load)}` : on.length ? `Across ${plural(on.length, 'machine')}` : 'No machine online'}
        trend={<Sparkline values={cpu} width={96} height={32} min={0} max={1} slot={CPU_SLOT} label={`Average CPU, recent samples, now ${formatPercent(load)}`} />}
      />
      <StatTile
        label="Memory in use"
        value={used}
        format={(n) => formatPercent(n)}
        sub={totalBytes ? `${formatBytes(usedBytes)} of ${formatBytes(totalBytes, 0)}` : 'No machine online'}
        trend={<Sparkline values={mem} width={96} height={32} min={0} max={1} slot={MEM_SLOT} label={`Average memory in use, now ${formatPercent(used)}`} />}
      />
      <StatTile
        label="Live sessions"
        value={live.length}
        sub={sessionCountsText(live)}
        visual={<StateBar counts={sessionStates(live)} />}
        onOpen={() => navigate('/sessions')}
      />
    </StatStrip>
  );
}
